import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'

import {
  Button, Link, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmAction } from '@functions/entity'

import usePublicationBooking from '@hooks/usePublicationBooking'
import usePublicationOrder from '@hooks/usePublicationOrder'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

const MODAL_KEY = 'ApprovePublicationBookingModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const renderArtworkLink = (artworkLink: string, isBulkRelease?: boolean) => {
  if (isBulkRelease) return 'download the artworks'

  return (
    <AuthenticatedDownload>
      <Link href={artworkLink} textProps={{ fontWeight: 'bold' }}>download the artwork</Link>
    </AuthenticatedDownload>
  )
}

const ApprovePublicationBookingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { isBulkRelease, publicationBooking: initPublicationBooking } = modalPayload

  const {
    callbacks: {
      approvePublicationBooking: approveFn,
    },
    publicationBooking,
    updating,
    urls: {
      downloadPDFUrl,
    },
  } = usePublicationBooking(initPublicationBooking)

  const {
    callbacks: {
      bulkReleasePublicationBookings: bulkReleaseFn,
    },
  } = usePublicationOrder({ id: publicationBooking.publication_order_id })

  const entityText = isBulkRelease ? 'Publication Bookings' : 'Publication Booking'

  const confirmApprovalPayload = {
    callbacks: {
      actionFn: isBulkRelease ? bulkReleaseFn : approveFn,
      afterAction: closeModal,
    },
    options: {
      icon: 'warning',
      isBulkAction: isBulkRelease,
    },
    strings: {
      actionString: 'release',
      confirmString: isBulkRelease
        ? 'Are you sure? This will approve all bookings and send all artworks to the provider.'
        : 'Are you sure? This will approve the booking and send the artwork to the provider.',
      entityName: entityText,
      toastText: `${entityText} Released`,
    },
  }

  const artworkLink = publicationBooking.id ? downloadPDFUrl : '#'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Approve"
        titleSecondLine={entityText}
      />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>
            <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />
            <Text color="orange" marginLeft="small" style={{ display: 'inline-block' }}>
              Notice
            </Text>
          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            Releasing the {
            isBulkRelease ? 'pages' : 'page'
            } will send all the artwork to the publication and upload it to their servers.
          </SidebarNoticeBox.Paragraph>

          <SidebarNoticeBox.Paragraph>
            This <strong>cannot</strong> be reversed.
          </SidebarNoticeBox.Paragraph>

          <SidebarNoticeBox.Paragraph>
            Please {renderArtworkLink(artworkLink, isBulkRelease)} to ensure that there are no errors.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Text color="bodyFontLightColor" fontSize="xsmall" lineHeight="1.3" marginTop="auto">
          Realhub accepts no responsibility for any errors including but not
          limited to, templates, colours, image quality and spelling mistakes.
        </Text>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => confirmAction(confirmApprovalPayload)}
          size="large"
        >
          I Agree, Release Booking {isBulkRelease ? 'Pages' : 'Page'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ApprovePublicationBookingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
